// src/components/SchoolAdmin/SchoolRegistration.tsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./SchoolRegistration.css";
import logo from "../../assets/dr-is-in.png";
import { AURA_GEOGRAPHY } from "../../data/auraGeography";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-backend-v12.onrender.com").replace(/\/+$/, "");
const raw = axios.create({ baseURL: API_BASE, timeout: 15000 });

function clean(v: any) {
  return String(v ?? "").trim();
}

function isLikelyEmail(v: string) {
  const s = clean(v);
  return !!s && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function pickErr(e: any): string {
  const detail = e?.response?.data?.detail;
  const message = e?.response?.data?.message;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (typeof message === "string" && message.trim()) return message;
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return e?.message || "Request failed";
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.map(clean).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

function getAllRegions(): string[] {
  return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.map((r) => r.name)));
}

function getCountriesByRegion(region: string): string[] {
  if (region === "ALL" || !region) {
    return uniqueSorted(AURA_GEOGRAPHY.flatMap((c) => c.regions.flatMap((r) => r.countries)));
  }

  for (const continent of AURA_GEOGRAPHY) {
    for (const reg of continent.regions) {
      if (reg.name === region) return uniqueSorted(reg.countries);
    }
  }

  return [];
}

function getRegionForCountry(country: string): string {
  const target = clean(country);
  if (!target) return "";

  for (const continent of AURA_GEOGRAPHY) {
    for (const region of continent.regions) {
      if (region.countries.includes(target)) return region.name;
    }
  }

  return "";
}

export default function SchoolRegistration() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);

  const [schoolName, setSchoolName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactTitle, setContactTitle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [stateValue, setStateValue] = useState("");
  const [zip, setZip] = useState("");
  const [region, setRegion] = useState("ALL");
  const [country, setCountry] = useState("ALL");
  const [district, setDistrict] = useState("");

  const [busySubmit, setBusySubmit] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const regionOptions = useMemo(() => getAllRegions(), []);
  const countryOptions = useMemo(() => getCountriesByRegion(region), [region]);

  const canSubmit = useMemo(() => {
    return (
      !!clean(schoolName) &&
      !!clean(contactName) &&
      isLikelyEmail(email) &&
      !!clean(address1) &&
      !!clean(city) &&
      !!clean(stateValue) &&
      !!clean(zip) &&
      country !== "ALL" &&
      region !== "ALL" &&
      !!clean(district)
    );
  }, [schoolName, contactName, email, address1, city, stateValue, zip, country, region, district]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");

    const payload = {
      school_name: clean(schoolName),
      contact_name: clean(contactName),
      contact_title: clean(contactTitle) || null,
      email: clean(email),
      phone: clean(phone) || null,
      address1: clean(address1),
      city: clean(city),
      state: clean(stateValue),
      zip: clean(zip),
      country: country === "ALL" ? "" : clean(country),
      region: region === "ALL" ? "" : clean(region),
      district: clean(district),
    };

    if (!payload.school_name) return setMsg("School Name is required.");
    if (!payload.contact_name) return setMsg("Primary Contact Name is required.");
    if (!isLikelyEmail(payload.email)) return setMsg("Please enter a valid email address.");
    if (!payload.address1) return setMsg("Address is required.");
    if (!payload.city) return setMsg("City is required.");
    if (!payload.state) return setMsg("State is required.");
    if (!payload.zip) return setMsg("ZIP is required.");
    if (!payload.region) return setMsg("Region is required.");
    if (!payload.country) return setMsg("Country is required.");
    if (!payload.district) return setMsg("District is required.");

    setBusySubmit(true);
    try {
      const res = await raw.post("/school/register", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const message =
        res?.data?.message ||
        "Application received. An Admin will review your request. If approved, you will receive your School ID and PIN to access the School Admin Portal.";

      setMsg(message);

      setSchoolName("");
      setContactName("");
      setContactTitle("");
      setEmail("");
      setPhone("");
      setAddress1("");
      setCity("");
      setStateValue("");
      setZip("");
      setRegion("ALL");
      setCountry("ALL");
      setDistrict("");
    } catch (e: any) {
      setMsg(pickErr(e) || "Registration failed");
    } finally {
      setBusySubmit(false);
    }
  };

  return (
    <div className="sr-page">
      {!acceptedDisclaimer && (
        <div className="disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="medical-disclaimer-title">
          <div className="disclaimer-modal">
            <img src={logo} alt="Doctor Is In App" className="disclaimer-logo" />

            <h2 id="medical-disclaimer-title">Important Platform Disclaimer</h2>

            <p>
              <strong>Doctor Is In App™</strong> is an educational, informational, and wellness support platform designed
              to help schools promote student wellbeing, provide access to wellness resources, and facilitate
              communication with qualified advisors.
            </p>

            <p>
              <strong>
                Doctor Is In App™ is not designed or intended to replace licensed physicians, psychologists, counselors,
                nurses, emergency services, or other qualified healthcare professionals outside the platform.
              </strong>
            </p>

            <p>
              School administrators should continue to follow all applicable school policies, safeguarding procedures,
              emergency protocols, referral processes, and applicable laws whenever a student&apos;s health, mental health, or
              safety is a concern.
            </p>

            <p>
              Artificial intelligence (AI) responses are intended for educational and informational purposes only and
              should <strong>never</strong> be relied upon as medical advice, diagnosis, treatment, or emergency guidance.
            </p>

            <p>
              <strong>Doctor Is In App™ is not intended for emergency situations.</strong> If a student or staff member is
              experiencing a medical or mental health emergency, immediately contact emergency services and follow your
              school&apos;s emergency response procedures.
            </p>

            <div className="disclaimer-small">
              By selecting <strong>I Agree</strong>, I acknowledge that I have read, understood, and accepted this disclaimer.
              <br />
              Last Updated: July 1, 2026
            </div>

            <div className="disclaimer-actions">
              <button type="button" onClick={() => setAcceptedDisclaimer(true)}>
                I Agree
              </button>

              <button type="button" className="secondary" onClick={() => navigate("/registration-portals")}>
                Cancel / Exit
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sr-card">
        <div className="sr-top">
          <img className="sr-logo" src={logo} alt="Doctor Is In" />
          <div className="sr-topText">
            <div className="sr-app">Doctor Is In</div>
            <div className="sr-tagline">AI Wellness Advisor with a human touch.</div>
          </div>
          <button className="sr-backBtn" type="button" onClick={() => navigate("/registration-portals")} disabled={busySubmit}>
            Back
          </button>
        </div>

        <div className="sr-titleRow">
          <div>
            <h1 className="sr-title">School Registration</h1>
            <div className="sr-subtitle">
              Submit your school application. If approved, the School Administrator will receive a School ID and PIN to log in and manage roster uploads.
            </div>
          </div>
        </div>

        {msg ? <div className="sr-msg">{msg}</div> : null}

        <form className="sr-form" onSubmit={submit}>
          <div className="sr-field">
            <label>School Name</label>
            <input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="e.g. Smith Middle School" disabled={busySubmit} />
          </div>

          <div className="sr-grid2">
            <div className="sr-field">
              <label>Primary Contact Name</label>
              <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="e.g. Jane Doe" disabled={busySubmit} />
            </div>

            <div className="sr-field">
              <label>Title optional</label>
              <input value={contactTitle} onChange={(e) => setContactTitle(e.target.value)} placeholder="e.g. Principal, Superintendent" disabled={busySubmit} />
            </div>
          </div>

          <div className="sr-grid2">
            <div className="sr-field">
              <label>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. admin@school.org" disabled={busySubmit} />
            </div>

            <div className="sr-field">
              <label>Phone optional</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. (401) 555-1212" disabled={busySubmit} />
            </div>
          </div>

          <div className="sr-field">
            <label>Address</label>
            <input value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="e.g. 123 Main Street" disabled={busySubmit} />
          </div>

          <div className="sr-grid2">
            <div className="sr-field">
              <label>City</label>
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Providence" disabled={busySubmit} />
            </div>

            <div className="sr-field">
              <label>State</label>
              <input value={stateValue} onChange={(e) => setStateValue(e.target.value)} placeholder="e.g. RI" disabled={busySubmit} />
            </div>
          </div>

          <div className="sr-grid2">
            <div className="sr-field">
              <label>ZIP</label>
              <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 02903" disabled={busySubmit} />
            </div>

            <div className="sr-field">
              <label>District</label>
              <input value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Providence Public Schools" disabled={busySubmit} />
            </div>
          </div>

          <div className="sr-grid2">
            <div className="sr-field">
              <label>Region</label>
              <select
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setCountry("ALL");
                }}
                disabled={busySubmit}
              >
                <option value="ALL">Select Region</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="sr-field">
              <label>Country</label>
              <select
                value={country}
                onChange={(e) => {
                  const nextCountry = e.target.value;
                  setCountry(nextCountry);

                  const inferredRegion = getRegionForCountry(nextCountry);
                  if (inferredRegion) setRegion(inferredRegion);
                }}
                disabled={busySubmit}
              >
                <option value="ALL">Select Country</option>
                {countryOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="sr-bottomRow">
            <button type="submit" className="sr-primaryWide" disabled={!canSubmit || busySubmit}>
              {busySubmit ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>

        <div className="sr-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
}
